<script lang="ts">
	import {
		getDate,
		isSameDay,
		isToday,
		isWithinInterval,
		isAfter,
		isBefore,
		getDay,
		lastDayOfMonth,
		isFirstDayOfMonth,
		isEqual
	} from 'date-fns';

	type PickerDateRange = {
		start: Date | undefined;
		end: Date | undefined;
	};

	export let selectedRange: PickerDateRange = {
		start: undefined,
		end: undefined
	};
	export let date: Date;
	export let previewDate: Date | undefined = undefined;

	type CoverPlacement = 'both' | 'left' | 'right' | 'none';

	function calculateCover(
		dayDate: Date,
		range: PickerDateRange,
		previewDate: Date | undefined
	): CoverPlacement {
		if (range.start && range.end) {
			if (isSameDay(dayDate, range.start)) return 'right';
			if (isSameDay(dayDate, range.end)) return 'left';
		}

		if (range.start && isSameDay(dayDate, range.start)) {
			if (previewDate) {
				if (isBefore(dayDate, previewDate)) return 'right';
				if (isAfter(dayDate, previewDate)) return 'left';

				if (isSameDay(dayDate, range.start)) return 'none';
			}
		}

		if (
			isBetweenSelection(dayDate, range) ||
			isBetweenPreviewSelection(dayDate, range, previewDate)
		) {
			if (previewDate && isSameDay(dayDate, previewDate)) {
				if (isEqual(dayDate, lastDayOfMonth(dayDate))) {
					return 'left';
				}
				if (isFirstDayOfMonth(dayDate)) {
					return 'right';
				}
				if (getDay(date) === 1) return 'right';
				if (getDay(date) === 0) return 'left';
				if (isBetweenSelection(dayDate, range)) return 'both';
				if (range.start && isBefore(dayDate, range.start)) return 'right';
				if (isBefore(dayDate, range.start!)) return 'right';
				if (isAfter(dayDate, range.start!)) return 'left';
			}

			// first and last day of week is rounded off
			if (getDay(date) === 1) return 'right';
			if (getDay(date) === 0) return 'left';
			if (isEqual(dayDate, lastDayOfMonth(dayDate))) {
				return 'left';
			}

			if (isFirstDayOfMonth(dayDate)) {
				return 'right';
			}
			return 'both';
		}

		return 'none';
	}

	function isSelected(dayDate: Date, range: PickerDateRange): boolean {
		if (range.start && isSameDay(dayDate, range.start)) return true;
		if (range.end && isSameDay(dayDate, range.end)) return true;

		return false;
	}

	function isBetweenSelection(dayDate: Date, range: PickerDateRange): boolean {
		if (!range.start || !range.end) return false;

		if (isSelected(dayDate, range)) return false;

		return isWithinInterval(dayDate, { start: range.start, end: range.end });
	}

	function isBetweenPreviewSelection(
		dayDate: Date,
		range: PickerDateRange,
		previewDate: Date | undefined
	): boolean {
		if (!previewDate || !range.start || range.end) return false;

		return isWithinInterval(dayDate, { start: range.start, end: previewDate });
	}

	$: cover = calculateCover(date, selectedRange, previewDate);
</script>

<div class="overflow-hidden z-0 relative">
	<button
		class="text-gray-1 w-9 h-9 bg-clip-content box-content m-0 p-0.75 rounded-full relativ"
		class:bg-magenta={isSelected(date, selectedRange)}
		class:text-white={isSelected(date, selectedRange)}
		class:bg-gray-10={isBetweenSelection(date, selectedRange) &&
			!(previewDate && isSameDay(date, previewDate))}
		class:bg-gray-11={isBetweenPreviewSelection(date, selectedRange, previewDate) ||
			(previewDate && isSameDay(date, previewDate))}
		class:left-cover={cover === 'both' || cover === 'left'}
		class:right-cover={cover === 'both' || cover === 'right'}
		class:cover-selected={selectedRange.start && selectedRange.end}
		class:cover-preview={selectedRange.start && !selectedRange.end && previewDate}
		{...$$restProps}
		on:click
		on:focus
		on:blur
		on:mouseenter
		on:mouseleave
	>
		{getDate(date)}
		{#if isToday(date)}
			<span class="w-9 h-9 left-0.75 top-0.75 absolute rounded-full border border-magenta"></span>
		{/if}
	</button>
</div>

<style lang="postcss" global>
	.right-cover {
		@apply after:content-[''] after:absolute after:w-6 after:h-9 after:right-0 after:top-0.75 after:z-[-1];
	}

	.left-cover {
		@apply before:content-[''] before:absolute  before:w-6 before:h-9 before:left-0 before:top-0.75 before:z-[-1];
	}

	.cover-preview {
		@apply before:bg-gray-11 after:bg-gray-11;
	}

	.cover-selected {
		@apply before:bg-gray-10 after:bg-gray-10;
	}
</style>
